import { h } from "@stencil/core";
import { ICON_NAMESPACE } from "../../icons/namespace";
/**
 * A wizard progression bar.
 * @part checkpoint - A checkpoint button
 * @part [state] - The current state of the checkpoint button.
 * @part blob - An indicator blob.
 * @part center - A central icon in the indicator blob.
 * @part connection - A connection between two checkpoints.
 */
export class Progression {
    constructor() {
        this.transformedCheckpoints = [];
        this.icon = (checkpoint, state) => checkpoint.icon?.(state) ?? this.icons?.[state];
        this.color = (checkpoint, state) => checkpoint.color?.(state) ??
            this.colors?.[state] ??
            this.defaultColors[state];
        this.defaultColors = {
            active: 'var(--color-secondary)',
            complete: 'var(--color-secondary)',
            inactive: 'var(--color-shade-30)',
        };
        this.checkpoints = undefined;
        this.location = undefined;
        this.readonly = undefined;
        this.colors = undefined;
        this.icons = undefined;
    }
    componentWillRender() {
        const activeIndex = this.checkpoints.findIndex(({ id }) => id === this.location);
        this.transformedCheckpoints = this.checkpoints.map((checkpoint, i) => {
            const active = checkpoint.id === this.location;
            const completed = activeIndex > i;
            const state = active
                ? 'active'
                : completed
                    ? 'complete'
                    : 'inactive';
            return {
                ...checkpoint,
                state,
                icon: this.icon(checkpoint, state),
                color: this.color(checkpoint, state),
                inactiveColor: this.color(checkpoint, 'inactive'),
            };
        });
    }
    render() {
        // Array is flattened so each child has a key.
        return this.transformedCheckpoints.flatMap(({ id, title, disabled = false, readonly = this.readonly ?? false, color, inactiveColor, icon, state, }, i) => {
            const next = this.transformedCheckpoints[i + 1];
            return [
                h("button", { key: id, type: 'button', part: `checkpoint ${state}`, class: {
                        readonly,
                        [state]: true,
                    }, disabled: disabled, tabindex: state === 'active' ? -1 : 0, onClick: () => readonly ? null : this.progressionRequest.emit(id), style: {
                        '--checkpoint-color': color,
                    } }, h("div", { class: 'blob', part: 'blob' }, h("es-icon", { part: 'center', class: { center: true, custom: !!icon }, icon: icon ?? [ICON_NAMESPACE, 'circle'], size: 16 })), title),
                !!next && (h("svg", { key: `${id}-${next.id}`, height: 30, width: 100, class: 'connection', part: 'connection' }, h("defs", null, h("linearGradient", { id: `stroke_${id}-${next.id}` }, h("stop", { offset: "0%", "stop-color": state === 'active'
                        ? inactiveColor
                        : color }), h("stop", { offset: "100%", "stop-color": next.color }))), h("rect", { x: 0, width: 100, y: 13, height: 4, fill: `url(#stroke_${id}-${next.id})` }))),
            ];
        });
    }
    static get is() { return "es-progression"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["es-progression.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["es-progression.css"]
        };
    }
    static get properties() {
        return {
            "checkpoints": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Checkpoint[]",
                    "resolved": "Checkpoint[]",
                    "references": {
                        "Checkpoint": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/es-progression/types.ts::Checkpoint"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A list of checkpoints to display."
                }
            },
            "location": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current active location."
                },
                "attribute": "location",
                "reflect": false
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Disable interactivity."
                },
                "attribute": "readonly",
                "reflect": false
            },
            "colors": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Partial<Record<CheckpointState, string>>",
                    "resolved": "undefined | { active?: string | undefined; complete?: string | undefined; inactive?: string | undefined; }",
                    "references": {
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "Record": {
                            "location": "global",
                            "id": "global::Record"
                        },
                        "CheckpointState": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/es-progression/types.ts::CheckpointState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Set custom colors for all checkpoints"
                }
            },
            "icons": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Partial<Record<CheckpointState, IconDescription>>",
                    "resolved": "undefined | { active?: IconDescription | undefined; complete?: IconDescription | undefined; inactive?: IconDescription | undefined; }",
                    "references": {
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "Record": {
                            "location": "global",
                            "id": "global::Record"
                        },
                        "CheckpointState": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/es-progression/types.ts::CheckpointState"
                        },
                        "IconDescription": {
                            "location": "import",
                            "path": "../es-icon/types",
                            "id": "src/components/es-icon/types.ts::IconDescription"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Set custom icons for all checkpoints"
                }
            }
        };
    }
    static get events() {
        return [{
                "method": "progressionRequest",
                "name": "progressionRequest",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a checkpoint is clicked."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
}
//# sourceMappingURL=es-progression.js.map
